import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StudentCalendarView from "./StudentCalendarView";
import { Calendar, Users } from "lucide-react";
import { toast } from "sonner";

interface Student {
  email: string;
  name: string | null;
  user_id?: string;
}

export default function InstructorStudentCalendar() {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadStudents();
    }
  }, [user]);

  const loadStudents = async () => {
    if (!user) return;

    try {
      // Get all unique students from tutoring schedules
      const { data: schedulesData, error: schedulesError } = await supabase
        .from('tutoring_schedules')
        .select('student_email, student_name')
        .eq('instructor_id', user.id);

      if (schedulesError) throw schedulesError;

      // Get unique students
      const uniqueStudents = Array.from(
        new Map(
          (schedulesData || []).map(s => [
            s.student_email,
            { email: s.student_email, name: s.student_name }
          ])
        ).values()
      );

      // Try to get user IDs for students using the database function
      const studentsWithIds = await Promise.all(
        uniqueStudents.map(async (student) => {
          try {
            const { data, error } = await supabase.rpc('get_user_id_from_email', {
              _email: student.email
            });

            if (error) {
              console.error('Error fetching user ID for', student.email, error);
              return { ...student, user_id: undefined };
            }

            return { ...student, user_id: data || undefined };
          } catch (error) {
            console.error('Error fetching user ID:', error);
            return { ...student, user_id: undefined };
          }
        })
      );

      setStudents(studentsWithIds);
      if (studentsWithIds.length > 0) {
        setSelectedStudent(studentsWithIds[0]);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">Loading students...</div>
        </CardContent>
      </Card>
    );
  }

  if (students.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            <CardTitle>Student Calendars</CardTitle>
          </div>
          <CardDescription>View and manage calendar events for your students</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No students with tutoring schedules yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          <CardTitle>Student Calendars</CardTitle>
        </div>
        <CardDescription>
          View and manage calendar events for your students. You'll see "Busy" for events you didn't create.
        </CardDescription>
        <div className="mt-4">
          <Select
            value={selectedStudent?.email}
            onValueChange={(email) => setSelectedStudent(students.find(s => s.email === email) || null)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a student" />
            </SelectTrigger>
            <SelectContent>
              {students.map((student) => (
                <SelectItem key={student.email} value={student.email}>
                  {student.name || student.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {selectedStudent?.user_id ? (
          <StudentCalendarView
            studentUserId={selectedStudent.user_id}
            studentName={selectedStudent.name || undefined}
          />
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>This student hasn't signed up yet. Calendar will be available once they create an account.</p>
            <p className="text-xs mt-2">Student email: {selectedStudent?.email}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
